/**
 * Ice Maze — what a selection means (specs/word-maze-mode.md §5).
 *
 * Kept out of MazeScene for the reason pairs/rules.ts gives: Phaser cannot be imported in the node
 * test environment, so anything left inside a scene is untestable.
 *
 * There is nothing on the board but the words that were asked for, so a selection is a word or it
 * is nothing, and **nothing on the board can cost a snowflake** — only the clock can. Reading a
 * word and deciding it is not the one is the work this mode is made of, and work must not be
 * charged for.
 */
import type { MazeBoard, Placement } from './grid';

/** A straight run of cells the player dragged, or tapped out letter by letter. */
export type Path = readonly [number, number][];

export type Selection =
  | { kind: 'target'; placement: Placement }
  | { kind: 'already'; placement: Placement }
  | { kind: 'none' };

/**
 * The cells between two ends, or null when they do not lie on one straight line.
 *
 * Any of the eight compass directions counts: the player may trace a word from either end — the
 * tap order is motor, not reading — and a diagonal in an early round simply finds nothing. What is
 * never *hidden* backwards is a different rule, and it lives in grid.directionsFor.
 */
export function lineBetween(
  from: readonly [number, number], to: readonly [number, number],
): Path | null {
  const dc = to[0] - from[0];
  const dr = to[1] - from[1];
  const len = Math.max(Math.abs(dc), Math.abs(dr));
  if (len === 0) return [[from[0], from[1]]];
  const straight = dc === 0 || dr === 0 || Math.abs(dc) === Math.abs(dr);
  if (!straight) return null;
  const sc = Math.sign(dc);
  const sr = Math.sign(dr);
  const path: [number, number][] = [];
  for (let i = 0; i <= len; i++) path.push([from[0] + sc * i, from[1] + sr * i]);
  return path;
}

/**
 * Tapping letter by letter: the path after tapping `cell`, given the path so far.
 *
 * This is the route a five-year-old actually takes — B, A, N, A, N, E, one tap each — and it has
 * to behave the way they expect at every step:
 *
 *  - The first tap starts a word.
 *  - Any tap in line with what is already selected extends it, filling in the cells between, so
 *    tapping the far end of a word takes it in one go and tapping each letter takes it in six.
 *  - Tapping the letter you last took removes it again — the undo a wandering finger needs.
 *  - Anything else starts a new word at that letter, which is what a child means when they give up
 *    on one line and point at another. Never an error, never a cost.
 */
export function tapPath(path: Path, cell: readonly [number, number]): Path {
  const [c, r] = cell;
  if (path.length === 0) return [[c, r]];

  const tail = path[path.length - 1];
  // Tapping the last letter again takes it back (and, at length 1, clears the selection).
  if (tail[0] === c && tail[1] === r) return path.slice(0, -1);
  // Tapping any letter already in the run trims back to it, so a long mis-tap is one tap to fix.
  const at = path.findIndex(([pc, pr]) => pc === c && pr === r);
  if (at >= 0) return path.slice(0, at + 1);

  if (path.length === 1) {
    const line = lineBetween(path[0], cell);
    return line ?? [[c, r]];
  }

  // Direction is fixed by the first two cells; the tap must lie further along that same ray.
  const dc = Math.sign(path[1][0] - path[0][0]);
  const dr = Math.sign(path[1][1] - path[0][1]);
  const ahead = lineBetween(tail, cell);
  if (ahead && ahead.length > 1) {
    const sc = Math.sign(ahead[1][0] - ahead[0][0]);
    const sr = Math.sign(ahead[1][1] - ahead[0][1]);
    if (sc === dc && sr === dr) return [...path, ...ahead.slice(1)];
  }
  return [[c, r]];
}

function key(path: Path): string {
  return path.map(([c, r]) => `${c},${r}`).join('|');
}

/** Does this path cover exactly the cells of that placement, from either end? */
function matches(path: Path, p: Placement): boolean {
  if (path.length !== p.path.length) return false;
  const k = key(path);
  return k === key(p.path) || k === key([...p.path].reverse());
}

/**
 * What the player just selected.
 *
 * `found` holds the ids already collected on this board, so re-tracing a frozen word is a no-op
 * rather than a second point.
 */
export function resolveSelection(board: MazeBoard, path: Path, found: ReadonlySet<string>): Selection {
  for (const p of board.targets) {
    if (matches(path, p)) {
      return found.has(p.word.id) ? { kind: 'already', placement: p } : { kind: 'target', placement: p };
    }
  }
  return { kind: 'none' };
}

/**
 * Flakes after a cleared board: clear one without losing any and one comes back, capped at the
 * starting count. Same refund, and the same reason, as the Pairs perfect-round brain: without it a
 * run is a one-way slide and the rounds past the ladder are unreachable.
 */
export function flakesAfterBoard(flakes: number, lostThisBoard: number, max: number): number {
  if (lostThisBoard > 0) return flakes;
  return Math.min(max, flakes + 1);
}
